// The user's own email groups (busy-my-web#274, AI Brain spec §10).
//
// The Email Workspace already lists GROUPS — but those are sender clusters the server computes on
// every request ("everything LinkedIn sent you"): nameless, unrenameable, and gone the moment the
// mail is archived. They stay exactly as they are; `lib/emailSession.js` is about them.
//
// This module is about the other kind, which did not exist: a collection a PERSON made out of
// threads they picked, from any number of senders, which survives archiving and can be split and
// merged. It has an id, a title and rows of its own in the mail service.
//
// The two are drawn in the same list and must never be confused, which is most of what is here: a
// row knows which kind it is, and every action is offered against the kind that can actually do it.
//
// Pure — no store, no router, nothing mounted.

import { lastUpdate } from '@/lib/emailSession'
import { threadsLabel } from '@/lib/emailThreadActions'

/** Verbatim labels, for the same reason lib/emailSession.js keeps its own. */
export const GROUP_TEXT = {
  heading: 'Email groups',
  view: 'View',
  createOngoing: 'Create ongoing',
  addToOngoing: 'Add to ongoing',
  archive: 'Archive group',
  unarchive: 'Restore group',
  split: 'Split',
  merge: 'Merge',
  splitPanelTitle: 'Split this group',
  splitHelper: 'Pick the threads that should move into a new group.',
  splitNameLabel: 'New group name:',
  mergePanelTitle: 'Merge into another group',
  mergeHelper: 'The threads move into the group you pick; this one is archived.',
  mergeEmpty: 'There is no other group to merge into.',
  brand: 'Suggested by Busy.me',
  empty: 'No email groups yet.',
}

/** The two authors of a group, matching the mail service's `Kind` column. */
export const USER_KIND = 'user'
export const BRAIN_KIND = 'brain'

/**
 * One group as a row.
 *
 * `isBrain` is drawn, not hidden: a group the Brain proposed is a suggestion the user may keep or
 * drop, and one they made is a decision — treating both as the same row would quietly present the
 * machine's guesses as the user's own filing.
 */
export function userGroupRow(group, now = new Date()) {
  const threads = Number(group?.threadCount ?? (group?.timelineIds || []).length) || 0
  return {
    id: group?.id || '',
    title: String(group?.title || '').trim() || 'Untitled group',
    threads,
    detail: threadsLabel(threads),
    isBrain: group?.kind === BRAIN_KIND,
    isArchived: !!group?.isArchived,
    updated: lastUpdate(group?.updatedUtc ?? group?.updated ?? null, now),
    timelineIds: [...(group?.timelineIds || [])],
  }
}

/** The whole listing, in the order the server ranked it (most recently changed first). */
export function userGroupRows(groups, now = new Date()) {
  return (groups || []).filter(Boolean).map(g => userGroupRow(g, now))
}

/**
 * What can be done with one group.
 *
 * Split needs at least two threads — a group of one cannot be partitioned, and offering the verb
 * would open a panel whose only possible answer is refused by the server. Merge needs somewhere to
 * merge INTO, so it is offered only when another live group exists. An archived group offers only
 * its own restoration: acting on a shelf the user has put away is not what they meant by putting it
 * away.
 */
// (The second argument — the other groups, for Merge's "is there anything to merge into" — is no
// longer read; callers may still pass it.)
export function groupActions(group) {
  const row = userGroupRow(group)
  if (!row.id) return []
  if (row.isArchived) {
    return [{ id: 'group-unarchive', text: GROUP_TEXT.unarchive, ready: true }]
  }

  // Two verbs (Figma 40009470-209128, design clarification 2026-09-02): View and Archive group.
  // Email groups are SYSTEM-made — grouped by contact — so the verbs that edited a collection by
  // hand (Create ongoing · Add to ongoing · Split · Merge) are not offered on a row any more. Their
  // plans (`splitPlan`, `mergeCandidates`) and store legs stay for the server rule they mirror;
  // nothing arms them from here.
  return [
    { id: 'group-view', text: GROUP_TEXT.view, ready: row.threads > 0 },
    { id: 'group-archive', text: GROUP_TEXT.archive, ready: true },
  ]
}

/**
 * What a split would do, mirroring the server's rule exactly (acc-account-sync-srv's
 * `EmailGroupRules.PlanSplit`): only threads the group holds can move, and a split that would empty
 * a side is not a split.
 *
 * Duplicated on purpose rather than asked of the server. The panel has to know whether its button
 * is pressable while the user is still ticking boxes, and a round-trip per tick to find out would
 * make the panel feel broken. The server still refuses an invalid split — this is the same rule
 * said early, not the rule itself.
 */
export function splitPlan(group, picked) {
  const have = [...(group?.timelineIds || [])]
  const wanted = new Set((picked || []).filter(Boolean))
  const moved = have.filter(id => wanted.has(id))
  const left = have.filter(id => !wanted.has(id))
  return { moved, left, isValid: moved.length > 0 && left.length > 0 }
}

/** The title a split's new group would get, mirroring the server's default. */
export function splitTitle(requested, sourceTitle) {
  const typed = String(requested || '').trim()
  if (typed) return typed
  const source = String(sourceTitle || '').trim()
  return source ? `${source} (split)` : 'Split group'
}

/** The groups this one could merge into — live, and never itself. */
export function mergeCandidates(groups, group) {
  const id = group?.id
  return (groups || [])
    .filter(g => g && g.id && g.id !== id && !g.isArchived)
    .map(g => ({ id: g.id, title: String(g.title || '').trim() || 'Untitled group' }))
}

/** The body `POST v1/email/groups/user` takes. */
export function createGroupRequest(title, timelineIds, kind = USER_KIND) {
  return {
    title: String(title || '').trim(),
    timelineIds: (timelineIds || []).filter(Boolean),
    kind,
  }
}

/** A group needs a name and nothing else — an empty group is a shelf waiting for threads. */
export function canCreateGroup(title) {
  return String(title || '').trim().length > 0
}

/**
 * What the name field starts as when a group is made from a selection.
 *
 * One thread names itself — its subject IS what the user picked, so it is true rather than invented.
 * Several threads have no honest shared name, so the field starts empty and the button waits for
 * one, exactly as the create-an-ongoing step does with several groups.
 */
export function suggestedGroupName(picks) {
  const list = (picks || []).filter(p => p && p.kind === 'email-pick')
  if (list.length !== 1) return ''
  return String(list[0].subject || list[0].title || '').trim()
}
